import { NaniumObject, Type } from 'nanium/objects';
import { CoreFile } from '../core/files/files.contractparts';
import { Dictionary } from '../core.contractpart';
import { Degree, Equivalence, FieldOfStudy } from "../anabin/degrees/degreetypes.contractpart";
import { AuthorizedUpload } from './extraUpload/upload.contractparts';

export class Day {
	@Type(String) year?: number;
	@Type(String) month?: number;
	@Type(String) day?: number;

	constructor(year?: number, month?: number, day?: number) {
		this.year = year;
		this.month = month;
		this.day = day;
	}

	toISOString(): string {
		if (!this.year || !this.month || !this.day) {
			return '';
		}
		const date = new Date(this.year, this.month, this.day); // Create JS Date (month is 0-based)
		return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
	}
}

export class MMYY {
	@Type(String) year?: number;
	@Type(String) month?: number;

	constructor(year?: number, month?: number) {
		this.year = year;
		this.month = month;
	}

}

export class Address {
	@Type(String) name: string;
	@Type(String) country: string;
	@Type(String) city: string;
	@Type(String) postalCode: string;
	@Type(String) address: string;
}

export class EntranceExam {
	@Type(Boolean) isCompleted: boolean;
	@Type(String) name: string;
	@Type(Day) date: Day;
}

export class EnglishProof extends NaniumObject<EnglishProof> {
	@Type(Boolean) hasKnowledge?: boolean;
	@Type(String) name?: string;
	@Type(String) level?: string;
	@Type(String) scoreSections?: string;
}

export class GermanProof extends NaniumObject<GermanProof> {
	@Type(Boolean) hasKnowledge?: boolean;
	@Type(String) name?: string;
	@Type(String) level?: string;
	@Type(String) scoreSections?: string;
	@Type(Date) issueDate?: Date;
	@Type(String) institution?: string;
	@Type(String) location?: string;

}

export class AcademicEducationInternalsInstitution extends NaniumObject<AcademicEducationInternalsInstitution> {
	@Type(String) anabinRecognition?: string;
	@Type(String) anabinId?: string;
	@Type(String) nameDE?: string;
	@Type(String) nameEN?: string;
}

export class AcademicEducationInternals extends NaniumObject<AcademicEducationInternals> {
	@Type(AcademicEducationInternalsInstitution) institution?: AcademicEducationInternalsInstitution;
	@Type(Degree) degree?: Degree;
	@Type(FieldOfStudy) programInfo?: FieldOfStudy;
	@Type(Array, Equivalence) equiv?: Equivalence[];
	@Type(String) apsComment?: string;
	@Type(Boolean) isQualifyingStudy?: boolean;
	@Type(Number) minGrade?: number;
	@Type(Number) maxGrade?: number;
	@Type(Number) studyYears?: number;
	@Type(Number) gradeEquivalent?: number;
	@Type(String) gradeNd?: string;
	@Type(Day) graduationDate?: Day;
	@Type(String) germanInstitutionName?: string;
	@Type(String) germanDegreeEquivalent?: string;

	@Type(String) degreeKind?: string;
	@Type(String) degreeNameLong?: string;
	@Type(String) degreeNameShort?: string;
	@Type(String) discipline?: string;
}

export class StudyField extends NaniumObject<StudyField> {
	@Type(String) degreeName: string;
	@Type(String) degreeId: string;
	@Type(String) programName: string;
	@Type(String) programId: string
}

export type KindOfStudy = 'bachelor' | 'master';

export class AcademicEducation extends NaniumObject<AcademicEducation> {
	@Type(String) id?: string;
	@Type(String) kindOfStudy?: KindOfStudy;
	@Type(StudyField) degreeInfo?: StudyField; // todo: actually this is unsecure can be modified by client (set the correct name but another id - can fake a better degree) - should be calculated on server (field change service) or at leased checked again on the server
	@Type(String) institution?: string;
	@Type(String) affiliatedInstitution?: string;
	@Type(String) city?: string;
	@Type(String) countryId?: string;
	@Type(MMYY) startDate?: MMYY;
	@Type(MMYY) endDate?: MMYY;
	@Type(String) GPA?: string;
	@Type(Boolean) graduatingUniversity?: boolean;
	@Type(Boolean) isGraduated?: boolean;
	@Type(String) creditsLeft?: string;
	@Type(Boolean) examsFinallyFailed?: boolean;
	@Type(String) failedModuleTitle?: string;
	@Type(AcademicEducationInternals) internals?: AcademicEducationInternals;
	@Type(Number) numberOfSemesters?: number; // only for study in germany
	@Type(String) cnic?: string;  // only for pakistan
	@Type(String) stampNumber?: string; // only for pakistan
	@Type(String) thesisTitle?: string;
}

export class SchoolEducation extends NaniumObject<SchoolEducation> {
	@Type(String) id: string;
	@Type(String) schoolName: string;
	@Type(String) city: string;
	@Type(MMYY) start: MMYY;
	@Type(MMYY) end: MMYY;
	@Type(String) GPA: string;
	@Type(String) certificateName: string;
	@Type(String) countryId: string;
}

export class PreparatoryCollege extends NaniumObject<PreparatoryCollege> {

	@Type(String) institution: string; // school name
	@Type(String) city: string;
	@Type(String) postalCode: string;
	@Type(Day) graduationDate: Day;
	@Type(Boolean) assessmentTestPassed: boolean;
}

export class WorkExperience extends NaniumObject<WorkExperience> {
	@Type(String) jobTitle?: string;
	@Type(String) location?: string;
	@Type(String) company?: string;
	@Type(MMYY) from?: MMYY;
	@Type(MMYY) to?: MMYY;
	@Type(String) description?: string
}


export class ApplicationError {
	@Type(String) text: string;
	@Type(Number) step: number;
	@Type(String) field?: keyof Application | string;

	constructor(text: string, step: number, field?: keyof Application | string) {
		this.text = text;
		this.step = step;
		this.field = field;
	}
}

export class ChatUser extends NaniumObject<ChatUser> {
	@Type(String) id: string;
	@Type(String) name: string;
	@Type(String) color?: string;
}

export type AdmissionDecision = 'Undecided' | 'No' | 'Yes' | 'Maybe';

export class PostEvaluation extends NaniumObject<PostEvaluation> {
	@Type(String) ranking?: string;
	@Type(Boolean) guteUni?: boolean;
	@Type(String) fachZul?: AdmissionDecision;
	@Type(Date) decisionDate?: Date;
	@Type(Number) numberPages?: number;
	@Type(Number) numberAuthors?: number;
	@Type(Number) references?: number;
	@Type(Number) structure?: number;
	@Type(Number) abstract?: number;
	@Type(Number) score?: number;
	@Type(String) comment?: string;
	@Type(Boolean) needBuddy?: boolean;
	@Type(Boolean) enrolled?: boolean;
	@Type(Boolean) inWebTool?: boolean;
}

export class ChatMessage extends NaniumObject<ChatMessage> {
	@Type(Date) timestamp: Date;
	@Type(ChatUser) from: ChatUser;
	@Type(String) message: string;
}

export type ApplicationCommentState = 'support-comment' | 'applicant-comment' | 'done';

export class ApplicationComment extends NaniumObject<ApplicationComment> {
	@Type(Array, ChatMessage) messages: ChatMessage[];
	@Type(String) state: ApplicationCommentState;
}

export const RefusalReasons = ['no HZB', 'no MZB', 'not in time', 'invalid documents', 'other'] as const;
export type RefusalReason = typeof RefusalReasons[number];

export class ApplicationInternals extends NaniumObject<ApplicationInternals> {
	@Type(String) reviserId?: string;

	// HZB
	@Type(String) universityPermissionCertificateType?: string;
	@Type(Number) universityPermissionCertificateGrade?: number;
	@Type(Day) universityPermissionCertificateDate?: Day;
	@Type(String) universityPermissionCountryId?: string;
	@Type(String) universityPermissionGermanCountyId?: string;
	@Type(Number) schoolYears?: number;
	@Type(String) preparatoryCollegeGrade?: string;

	// export
	@Type(Date) exportDate?: Date;
	@Type(String) exportId?: string;
	@Type(String) exportFileId?: string;

	// comment fields
	@Type(Array, String) universityQualificationSummary?: string[];
	@Type(Array, String) masterQualificationSummary?: string[];
	@Type(Array, String) languageQualificationSummary?: string[];
	@Type(Array, String) otherQualificationSummary?: string[];
	@Type(String) remarks?: string;
	@Type(String) admissionRequirement?: string;
	@Type(String) reviserRemark?: string;
	@Type(Array, CoreFile) documents?: CoreFile[];

	//post evaluation
	@Type(PostEvaluation) postEvaluation?: PostEvaluation;
}

export type AdditionalFileCategory =
	'motivationVideo'
	| 'letterOfMotivation'
	| 'letterOfRecommendation'
	| 'other';

export class AdditionalFile extends NaniumObject<AdditionalFile> {
	@Type(String) category: AdditionalFileCategory;
	@Type(String) description: string;
	@Type(CoreFile) file: CoreFile;
}

export class ApplicationPayment extends NaniumObject<ApplicationPayment> {
	@Type(String) paypalOrderId: string;
	@Type(String) paypalTransactionId?: string;
	@Type(String) payerName: string;
	@Type(String) amount: string;
	@Type(String) currency: string;
	@Type(Date) paymentDate: Date;
	@Type(Boolean) bankTransferAuthorized?: boolean;
	@Type(Boolean) bankTransferDone?: boolean;
	@Type(Date) serviceProvisionDate?: Date;
	@Type(String) invoiceNumber?: string;
	@Type(Date) invoiceDate?: Date;
	@Type(String) invoiceFileId?: string;
	@Type(Number) grossPrice?: number;
	@Type(Number) taxPercent?: number;
}

export type EvaluationResult = 'positive' | 'negative';

export class Application extends NaniumObject<Application> {
	@Type(String) id?: string;
	@Type(String) applicationNumber?: number;
	@Type(String) accountId?: string;
	@Type(String) studyProgrammId?: string;
	@Type(String) currentStep?: number;
	@Type(Date) creationDate?: Date;
	@Type(Date) lastChangeDate?: Date;
	@Type(Date) lastApplicantChangeDate: Date;
	@Type(Date) dateOfReceipt?: Date;
	@Type(Date) evaluationDate?: Date;
	@Type(Date) cancellationDate?: Date;
	@Type(String) state: ApplicationState;
	@Type(String) evaluationResult?: EvaluationResult;
	@Type(String) evaluationResultReason?: RefusalReason;
	@Type(String) evaluationResultReasonText?: string;

	@Type(Object, CoreFile) files: Dictionary<CoreFile>;
	@Type(Array, AdditionalFile) additionalFiles: AdditionalFile[];

	@Type(String) title?: string;
	@Type(String) salutation?: string;
	@Type(String) identificationNumber?: string;
	@Type(String) firstname?: string;
	@Type(String) additionalName: string;
	@Type(String) surname?: string;
	@Type(String) gender?: 'Male' | 'Female' | 'Other';
	@Type(Day) birthdate?: Day;
	@Type(String) birthplace?: string;
	@Type(String) country?: string;
	@Type(String) nationality?: string;
	@Type(String) phoneNumber: string;

	@Type(String) germanGrade?: GermanProof;
	@Type(String) germanCertificateId?: string;
	@Type(EnglishProof) englishGrade?: EnglishProof;

	@Type(Boolean) currentlyAttendingGerman?: boolean
	@Type(Boolean) startedOrCompletedStudyGermany?: boolean

	@Type(Boolean) applyingToPreparatoryCollege?: boolean
	@Type(PreparatoryCollege) preparatoryCollege?: PreparatoryCollege;

	@Type(SchoolEducation) schoolBackground?: SchoolEducation;
	@Type(Array, AcademicEducation) academicBackground?: AcademicEducation[];
	@Type(Array, WorkExperience) workBackground?: WorkExperience[];

	@Type(Address) correspondenceAddress?: Address;
	@Type(EntranceExam) entranceExam?: EntranceExam;

	@Type(Array, ApplicationError) errors?: ApplicationError[];
	@Type(Object, ApplicationComment) fieldComments?: Dictionary<ApplicationComment>;
	// @Type(Object, ApplicationComment) stepComments?: Dictionary<ApplicationComment>;
	@Type(ApplicationInternals) internals?: ApplicationInternals;
	@Type(ApplicationPayment) payment?: ApplicationPayment;

	@Type(Boolean) trueAndCompleteConfirmation?: boolean;
	@Type(Array, String) infoSource?: string[];
	@Type(String) infoSourceOther?: string;
	@Type(Array, AuthorizedUpload) authorizedUploads: AuthorizedUpload[];
}

export const schoolLeavingCertificateTypes: SchoolLeavingCertificateTypes[] = [
	{ id: '70', name: 'bisheriger Studienabschluss', hisInOneUniqName: '54' },
	{ id: '67', name: 'Studienkolleg (Glauchau/Leipzig)', hisInOneUniqName: '56' },
	{ id: '69', name: 'berufliche Aufstiegsfortbildung', hisInOneUniqName: '58' },
	{ id: '21', name: 'Studienkolleg(fgHR)', hisInOneUniqName: '51' },
	{ id: '1', name: 'allg. HZB in Deutschland', hisInOneUniqName: '03' },
	{ id: '15', name: 'allg. HZB im Ausland', hisInOneUniqName: '39' },
	{ id: '16', name: 'fgb. HZB in Deutschland', hisInOneUniqName: '43' },
	{ id: '25', name: 'fgb. HZB im Ausland', hisInOneUniqName: '59' },
]

export interface SchoolLeavingCertificateTypes {
	id: string;
	name: string;
	hisInOneUniqName: string;
}

export const ApplicationStates = [
	'new', 'review 1', 'query asked', 'query answered', 'evaluated', 'canceled'
] as const;
export type ApplicationState = typeof ApplicationStates[number];

export const UploadCategories: {
	id: AdditionalFileCategory,
	nameDE: string;
	nameEN: string;
}[] = [
	{ id: 'motivationVideo', nameDE: 'Motivationsvideo', nameEN: 'motivation video' },
	{ id: 'letterOfMotivation', nameDE: 'Motivationsschreiben', nameEN: 'letter of motivation' },
	{ id: 'letterOfRecommendation', nameDE: 'Empfehlungsschreiben', nameEN: 'letter of recommendation' },
	// { id: 'curriculumVitae', nameDE: 'Lebenslauf', nameEN: 'curriculum vitae' },
	// { id: 'copyOfPassport', nameDE: 'Passkopie', nameEN: 'copy of passport' },
	// { id: 'languageCertificate', nameDE: 'Sprachzeugnis', nameEN: 'language certificate' },
	// { id: 'studyCertificate', nameDE: 'Studienzeugnis', nameEN: 'study certificate' },
	// { id: 'schoolReport', nameDE: 'Schulzeugnis', nameEN: 'school report' },
	// { id: 'workSample', nameDE: 'Arbeitsprobe', nameEN: 'work sample' },
	// { id: 'proofOfEmployment', nameDE: 'Tätigkeitsnachweis/Praktikum', nameEN: 'proof of employment/internship' },
	{ id: 'other', nameDE: 'Sonstiges', nameEN: 'other document' },
];
